import * as PIXI from "pixi.js";

class WebGLLightning {
  constructor(app) {
    this.app = app;
    this.container = new PIXI.Container();
    this.bolts = [];
    this.time = 0;
    this.init();
  }

  init() {
    const boltCount = 3;

    for (let i = 0; i < boltCount; i++) {
      this.createLightningBolt();
    }

    this.updateBound = () => this.update();
    this.app.ticker.add(this.updateBound);
  }

  createLightningBolt() {
    const bolt = {
      graphics: new PIXI.Graphics(),
      life: 1.0,
      intensity: Math.random() * 0.5 + 0.5,
      decay: Math.random() * 0.02 + 0.01,
      segments: this.generateLightningPath(),
      flashTime: Math.random() * Math.PI * 2,
    };

    this.bolts.push(bolt);
    this.container.addChild(bolt.graphics);
  }

  generateLightningPath() {
    const segments = [];
    const startX = Math.random() * this.app.screen.width;
    const startY = -50; // Start above the screen
    const endX = startX + (Math.random() - 0.5) * 600; // More horizontal spread
    const endY = this.app.screen.height + 50; // End below the screen

    const segmentCount = 12;

    for (let i = 0; i <= segmentCount; i++) {
      const t = i / segmentCount;
      let x = startX + (endX - startX) * t;
      let y = startY + (endY - startY) * t;

      // Add jagged deviation
      if (i > 0 && i < segmentCount) {
        x += (Math.random() - 0.5) * 80;
        y += (Math.random() - 0.5) * 40;
      }

      segments.push({ x, y });
    }

    return segments;
  }

  update() {
    this.time += 0.016;

    this.bolts.forEach((bolt, boltIndex) => {
      // Update bolt life
      bolt.life -= bolt.decay;

      // Respawn bolt when it dies
      if (bolt.life <= 0) {
        bolt.life = 1.0;
        bolt.segments = this.generateLightningPath();
        bolt.flashTime = Math.random() * Math.PI * 2;
      }

      // Calculate flicker effect
      const flicker = Math.random() > 0.7 ? Math.random() * 0.5 + 0.5 : 1.0;
      const pulse = Math.sin(this.time * 8 + bolt.flashTime) * 0.3 + 0.7;
      const finalIntensity = bolt.intensity * bolt.life * flicker * pulse;

      // Clear and redraw bolt
      bolt.graphics.clear();

      if (bolt.segments.length > 1) {
        // Draw main bolt
        bolt.graphics.moveTo(bolt.segments[0].x, bolt.segments[0].y);

        for (let i = 1; i < bolt.segments.length; i++) {
          bolt.graphics.lineTo(bolt.segments[i].x, bolt.segments[i].y);
        }

        bolt.graphics.stroke({
          width: 3 + finalIntensity * 2,
          color: 0xadd8e6,
          alpha: finalIntensity,
        });

        // Draw glow effect
        bolt.graphics.moveTo(bolt.segments[0].x, bolt.segments[0].y);

        for (let i = 1; i < bolt.segments.length; i++) {
          bolt.graphics.lineTo(bolt.segments[i].x, bolt.segments[i].y);
        }

        bolt.graphics.stroke({
          width: 8 + finalIntensity * 4,
          color: 0x87ceeb,
          alpha: finalIntensity * 0.3,
        });

        // Add branches occasionally
        if (Math.random() > 0.85) {
          const branchIndex =
            Math.floor(Math.random() * (bolt.segments.length - 2)) + 1;
          const branchStart = bolt.segments[branchIndex];
          const branchEnd = {
            x: branchStart.x + (Math.random() - 0.5) * 100,
            y: branchStart.y + Math.random() * 60 + 20,
          };

          bolt.graphics.moveTo(branchStart.x, branchStart.y);
          bolt.graphics.lineTo(branchEnd.x, branchEnd.y);
          bolt.graphics.stroke({
            width: 2,
            color: 0xadd8e6,
            alpha: finalIntensity * 0.7,
          });
        }
      }

      // Screen flash effect for intense lightning
      if (finalIntensity > 0.8) {
        // You could add a screen flash effect here
        // For now, we'll just make the bolt extra bright
        bolt.graphics.filters = [new PIXI.BlurFilter(2)];
      } else {
        bolt.graphics.filters = [];
      }
    });
  }

  destroy() {
    try {
      if (this.updateBound && this.app && this.app.ticker) {
        this.app.ticker.remove(this.updateBound);
        this.updateBound = null;
      }

      this.bolts = [];

      if (this.container) {
        this.container.destroy({ children: true, texture: true });
        this.container = null;
      }
    } catch (e) {
      console.warn("Error destroying WebGLLightning:", e);
    }
  }
}

export default WebGLLightning;
